import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { EMPTY, from, type Observable } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { UserApiService } from 'src/api/user'
import { environment } from 'src/environments/environment'
import { UsersService } from 'src/services/data/users.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { archiveUser, createUser, createUserFailure, createUserSuccess, deleteUser, deleteUserFailure, deleteUserSuccess, disable2FAUser, enable2FAUser, loadUsers, loadUsersFailure, loadUsersSuccess, unarchiveUser, updateUser, updateUserFailure, updateUserSuccess } from './user.actions'

const { tradecafeAccount } = environment

@Injectable()
export class UserEffects {
  private actions$ = inject(Actions)
  private users = inject(UsersService)
  private toaster = inject(ToasterService)
  private UserApi = inject(UserApiService)


  loadUsers$ = createEffect(() => this.actions$.pipe(
    ofType(loadUsers),
    switchMap(action =>
      from(this.users.getUsers(action.account || tradecafeAccount, typeof action.archived === 'undefined'? 1: action.archived, action.fields)).pipe(
        map(r => loadUsersSuccess({ users: r.data, fields: action.fields as string[] })),
        catchAndLog(this.toaster, 'Unable to fetch users', loadUsersFailure
      )))))

  createUser$ = createEffect(() => this.actions$.pipe(
    ofType(createUser),
    tap(() => this.toaster.progress('Creating user...')),
    switchMap(action =>
      from(this.UserApi.create(action.account, action.user)).pipe(
        map(r => createUserSuccess({ user: r.data })),
        tap(() => this.toaster.success('User created successfully.')),
        catchAndLog(this.toaster, 'Unable to create user', createUserFailure
      )))))

  updateUser$ = createEffect(() => this.actions$.pipe(
    ofType(updateUser),
    tap(() => this.toaster.progress('Updating user...')),
    switchMap(action =>
      from(this.UserApi.update(action.account, action.id, action.user)).pipe(
        map(r => updateUserSuccess({ user: r.data })),
        tap(() => this.toaster.success('User updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update user', updateUserFailure
      )))))

  deleteUser$ = createEffect(() => this.actions$.pipe(
    ofType(deleteUser),
    tap(() => this.toaster.progress('Deleting user...')),
    switchMap(action =>
      from(this.UserApi.delete(action.account, action.id)).pipe(
        map(r => deleteUserSuccess({ id: action.id, account: action.account })),
        tap(() => this.toaster.success('User deleted successfully.')),
        catchAndLog(this.toaster, 'Unable to delete user', deleteUserFailure
      )))))

  archiveUser$ = createEffect(() => this.actions$.pipe(
    ofType(archiveUser),
    tap(() => this.toaster.progress('Archiving user...')),
    switchMap(action =>
      from(this.UserApi.update(action.account, action.user_id, { archived: true as any as number})).pipe(
        map(r => updateUserSuccess({ user: r.data })),
        tap(() => this.toaster.success('User archived successfully.')),
        catchAndLog(this.toaster, 'Unable to update user', updateUserFailure
      )))))

  unarchiveUser$ = createEffect(() => this.actions$.pipe(
    ofType(unarchiveUser),
    tap(() => this.toaster.progress('Unarchiving user...')),
    switchMap(action =>
      from(this.UserApi.update(action.account, action.user_id, { archived: false })).pipe(
        map(r => updateUserSuccess({ user: r.data })),
        tap(() => this.toaster.success('User unarchived successfully.')),
        catchAndLog(this.toaster, 'Unable to update user', updateUserFailure
      )))))

  enable2FAUser$ = createEffect(() => this.actions$.pipe(
    ofType(enable2FAUser),
    tap(() => this.toaster.progress('Enabling 2FA for user...')),
    switchMap(action =>
      from(this.UserApi.update(action.account, action.user_id, { autocomplete_2fa: false })).pipe(
        map(r => updateUserSuccess({ user: r.data })),
        tap(() => this.toaster.success('2FA enabled successfully.')),
        catchAndLog(this.toaster, 'Unable to update user', updateUserFailure
      )))))

  disable2FAUser$ = createEffect(() => this.actions$.pipe(
    ofType(disable2FAUser),
    tap(() => this.toaster.progress('Disabling 2FA for user...')),
    switchMap(action =>
      from(this.UserApi.update(action.account, action.user_id, { autocomplete_2fa: true })).pipe(
        map(r => updateUserSuccess({ user: r.data })),
        tap(() => this.toaster.success('2FA disabled successfully.')),
        catchAndLog(this.toaster, 'Unable to update user', updateUserFailure
      )))))

  invalidateUsersCache$ = createEffect(() => this.actions$.pipe(
    ofType(createUserSuccess, updateUserSuccess),
    tap(() => this.users.clearCache()),
    switchMap(() => EMPTY as Observable<any>)))
  invalidateDeletedUsersCache$ = createEffect(() => this.actions$.pipe(
    ofType(deleteUserSuccess),
    tap(() => this.users.clearCache()),
    switchMap(() => EMPTY as Observable<any>)))
}
